/**
 * ログインフォーム。
 *
 * - email + password で Cognito User Pool にサインインする。
 * - ログイン成功後は `useAuth` 経由で AuthProvider state が更新され、
 *   App.tsx 側のガードによって BroadcastPointsPage が表示される。
 * - アカウントは「ユーザー管理」からの招待でのみ作成される (自己登録の導線は無い)。
 * - パスワードリセット完了後の遷移時は、リセット済みメールアドレスを
 *   location.state で受け取り、初期値として埋める。
 */
import { Spinner } from '../components/Spinner';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';

import { APP_TITLE } from '../constants/appInfo';

import { useAuth } from './AuthProvider';
import { readLoginPrefill } from './loginPrefill';

interface LoginRedirectState {
  /** パスワードリセット完了後にリセット済みメールアドレスを受け取る */
  resetEmail?: string;
}

export function LoginPage(): React.JSX.Element {
  const { signIn, error: authError, isLoading } = useAuth();
  const location = useLocation();
  const redirectState = location.state as LoginRedirectState | null;
  const resetEmail = redirectState?.resetEmail;
  // PR レビュー環境専用: `?email=...&password=...` による初期入力 (本番 / stg ビルドでは無効)
  const [prefill] = useState(() => readLoginPrefill(location.search));
  const initialEmail = resetEmail ?? prefill.email;

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(prefill.password);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState<string | null>(
    resetEmail
      ? 'パスワードの再設定が完了しました。新しいパスワードでログインしてください。'
      : null,
  );

  // location.state が変化したら同期する (パスワードリセット完了直後を想定)
  useEffect(() => {
    if (resetEmail) {
      setEmail(resetEmail);
      setInfo('パスワードの再設定が完了しました。新しいパスワードでログインしてください。');
    }
  }, [resetEmail]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setInfo(null);
    try {
      await signIn(email, password);
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <h1>{APP_TITLE}</h1>
        <form onSubmit={handleSubmit}>
          <label>
            <span className="required">メールアドレス</span>
            <input
              type="email"
              data-testid="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting || isLoading}
            />
          </label>
          <label>
            <span className="required">パスワード</span>
            <input
              type="password"
              data-testid="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting || isLoading}
            />
          </label>
          {info ? <div className="info-message">{info}</div> : null}
          {authError ? <div className="error-message">{authError}</div> : null}
          {/*
            ボタンが押せない理由を示す。灰色のボタンだけでは「なぜ押せないのか」が
            分からず、故障と誤解されやすいため (入力済みなら非表示にする)。
          */}
          {!email || !password ? (
            <div className="form-disabled-reason" data-testid="login-disabled-reason">
              メールアドレスとパスワードを入力するとログインできます
            </div>
          ) : null}
          <button
            type="submit"
            data-testid="login-submit"
            className="primary"
            disabled={submitting || isLoading || !email || !password}
          >
            {submitting ? (
              <>
                <Spinner />
                ログイン中...
              </>
            ) : (
              'ログイン'
            )}
          </button>
          <div className="login-footer">
            パスワードをお忘れの方は <Link to="/reset-password">パスワードをリセット</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
